import type {
  AuditLogEntry,
  ContentFieldChange,
} from '@rossum/api-client/auditLogs';
import type {
  ActivityRow,
  ActivityRowContext,
  FieldChangeRow,
} from '../../core';

export const toFieldRows = (
  entry: AuditLogEntry,
  { entryIndex }: ActivityRowContext
): ActivityRow[] => {
  const { content } = entry.content.changes;
  if (!content) return [];

  const labels = content.meta?.schemaIdLabels ?? {};
  const label = (schemaId: string) => labels[schemaId] ?? schemaId;
  const keyPrefix = `${entry.timestamp}:${entryIndex}`;

  const toField = (
    change: ContentFieldChange,
    key: string
  ): FieldChangeRow => ({
    kind: 'field',
    key,
    schemaId: change.schemaId,
    label: label(change.schemaId),
    previousValue: change.previousValue,
    value: change.value,
  });

  // Keys carry the wire index, so filtering later never shifts a surviving row's key.
  const fields = content.data.fields.map((change, index) =>
    toField(change, `${keyPrefix}:${change.schemaId}:${index}`)
  );

  const tables = (content.data.tables ?? []).flatMap((table): ActivityRow[] => {
    const rows = table.rows.flatMap(row => {
      const cells = row.fields.map((change, index) =>
        toField(
          change,
          `${keyPrefix}:${table.schemaId}:${row.rowId}:${change.schemaId}:${index}`
        )
      );
      return cells.length > 0 ? [{ rowId: row.rowId, cells }] : [];
    });
    return rows.length > 0
      ? [
          {
            kind: 'table',
            key: `${keyPrefix}:${table.schemaId}`,
            schemaId: table.schemaId,
            label: label(table.schemaId),
            rows,
          },
        ]
      : [];
  });

  return [...fields, ...tables];
};
